import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Layout variants for the two main public pages. Picking a variant swaps the
// whole UI/UX of the page without touching any content — so a cloned project
// pointed at a new database can look substantially different.

export type NavigatorLayout = 'classic' | 'wizard';
export type ApartmentDetailsLayout = 'classic' | 'sticky';

export interface PageLayouts {
  navigator: NavigatorLayout;
  apartmentDetails: ApartmentDetailsLayout;
  updatedAt?: Date;
}

export interface LayoutOption<T extends string> {
  value: T;
  label: string;
  description: string;
}

export const NAVIGATOR_LAYOUTS: LayoutOption<NavigatorLayout>[] = [
  {
    value: 'classic',
    label: 'Класически',
    description: 'Фасадата отгоре, етажните планове под нея — всичко на един скрол.',
  },
  {
    value: 'wizard',
    label: 'Стъпков избор',
    description:
      'Води клиента стъпка по стъпка: вход → етаж → обект, с лента за напредък. Подходящо за комплекси с няколко входа.',
  },
];

export const APARTMENT_DETAILS_LAYOUTS: LayoutOption<ApartmentDetailsLayout>[] = [
  {
    value: 'classic',
    label: 'Класически',
    description: 'Галерия отляво, всички данни в карта отдясно.',
  },
  {
    value: 'sticky',
    label: 'Лепкав панел с цена',
    description:
      'Широка галерия и съдържание отляво, а цената, статусът и бутонът за запитване стоят в панел отдясно, който следва скролването.',
  },
];

const defaultLayouts: PageLayouts = {
  navigator: 'classic',
  apartmentDetails: 'classic',
};

const isNavigatorLayout = (v: unknown): v is NavigatorLayout =>
  NAVIGATOR_LAYOUTS.some((o) => o.value === v);

const isDetailsLayout = (v: unknown): v is ApartmentDetailsLayout =>
  APARTMENT_DETAILS_LAYOUTS.some((o) => o.value === v);

export const usePageLayouts = () => {
  const [layouts, setLayouts] = useState<PageLayouts>(defaultLayouts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, 'content', 'pageLayouts'));
      if (snap.exists()) {
        const data = snap.data();
        setLayouts({
          // Unknown values fall back to classic so a bad write can never blank a page.
          navigator: isNavigatorLayout(data.navigator) ? data.navigator : 'classic',
          apartmentDetails: isDetailsLayout(data.apartmentDetails)
            ? data.apartmentDetails
            : 'classic',
          updatedAt: data.updatedAt?.toDate(),
        });
      } else {
        setLayouts(defaultLayouts);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching page layouts:', err);
      setError('Грешка при зареждане на оформленията');
      setLayouts(defaultLayouts);
    } finally {
      setLoading(false);
    }
  };

  const saveLayouts = async (next: Partial<PageLayouts>) => {
    try {
      const merged = { ...layouts, ...next };
      await setDoc(doc(db, 'content', 'pageLayouts'), {
        navigator: merged.navigator,
        apartmentDetails: merged.apartmentDetails,
        updatedAt: serverTimestamp(),
      });
      setLayouts({ ...merged, updatedAt: new Date() });
      return true;
    } catch (err) {
      console.error('Error saving page layouts:', err);
      setError('Грешка при запазване на оформленията');
      return false;
    }
  };

  useEffect(() => {
    fetchLayouts();
  }, []);

  return { layouts, loading, error, saveLayouts, refreshLayouts: fetchLayouts };
};
